/**
 * Polygon helpers for the floor-plan importer.
 *
 * Polygons are arrays of [x, y] pairs. Two coordinate spaces are in play:
 *   - NORMALISED (0..1) relative to the source plan image — what the importer
 *     stores and what the review overlay draws in.
 *   - MALL-SPACE METRES, centred on the plan's midpoint — what the 3D scene
 *     consumes. `toMallSpace` is the only place the two meet.
 */

/** Shoelace area. Positive for counter-clockwise in a y-down raster. */
export function signedArea(poly) {
    let a = 0;
    for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
        a += (poly[j][0] * poly[i][1]) - (poly[i][0] * poly[j][1]);
    }
    return a / 2;
}

export const area = (poly) => Math.abs(signedArea(poly));

export function centroid(poly) {
    let cx = 0;
    let cy = 0;
    let a = 0;
    for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
        const f = (poly[j][0] * poly[i][1]) - (poly[i][0] * poly[j][1]);
        a += f;
        cx += (poly[j][0] + poly[i][0]) * f;
        cy += (poly[j][1] + poly[i][1]) * f;
    }
    a *= 0.5;
    if (Math.abs(a) < 1e-12) {
        // Degenerate ring — fall back to the vertex average.
        const n = poly.length || 1;
        return [poly.reduce((s, p) => s + p[0], 0) / n, poly.reduce((s, p) => s + p[1], 0) / n];
    }
    return [cx / (6 * a), cy / (6 * a)];
}

export function bounds(poly) {
    let minX = Infinity; let minY = Infinity; let maxX = -Infinity; let maxY = -Infinity;
    poly.forEach(([x, y]) => {
        if (x < minX) minX = x;
        if (y < minY) minY = y;
        if (x > maxX) maxX = x;
        if (y > maxY) maxY = y;
    });
    return { minX, minY, maxX, maxY, w: maxX - minX, h: maxY - minY };
}

export function pointInPolygon(pt, poly) {
    const [x, y] = pt;
    let inside = false;
    for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
        const [xi, yi] = poly[i];
        const [xj, yj] = poly[j];
        const intersects = ((yi > y) !== (yj > y))
            && (x < ((xj - xi) * (y - yi)) / ((yj - yi) || 1e-12) + xi);
        if (intersects) inside = !inside;
    }
    return inside;
}

const perpDistance = (p, a, b) => {
    const dx = b[0] - a[0];
    const dy = b[1] - a[1];
    const len = Math.hypot(dx, dy);
    if (len < 1e-12) return Math.hypot(p[0] - a[0], p[1] - a[1]);
    return Math.abs((dy * p[0]) - (dx * p[1]) + (b[0] * a[1]) - (b[1] * a[0])) / len;
};

/**
 * Ramer–Douglas–Peucker. A traced contour has one vertex per boundary PIXEL —
 * thousands per store — which is both unusable in the editor and ruinous to
 * extrude. This is what turns a staircase of pixels back into wall lines.
 */
export function simplify(poly, epsilon) {
    if (poly.length < 3) return poly.slice();

    const keep = new Uint8Array(poly.length);
    keep[0] = 1;
    keep[poly.length - 1] = 1;
    const stack = [[0, poly.length - 1]];

    while (stack.length) {
        const [first, last] = stack.pop();
        let maxD = 0;
        let index = -1;
        for (let i = first + 1; i < last; i++) {
            const d = perpDistance(poly[i], poly[first], poly[last]);
            if (d > maxD) { maxD = d; index = i; }
        }
        if (maxD > epsilon && index > 0) {
            keep[index] = 1;
            stack.push([first, index], [index, last]);
        }
    }

    const out = [];
    for (let i = 0; i < poly.length; i++) if (keep[i]) out.push(poly[i]);
    return out;
}

/** Drop vertices that sit almost exactly on the line between their neighbours. */
export function dropCollinear(poly, tolerance = 1e-4) {
    if (poly.length < 4) return poly.slice();
    const out = [];
    for (let i = 0; i < poly.length; i++) {
        const prev = poly[(i - 1 + poly.length) % poly.length];
        const cur = poly[i];
        const next = poly[(i + 1) % poly.length];
        if (perpDistance(cur, prev, next) > tolerance) out.push(cur);
    }
    return out.length >= 3 ? out : poly.slice();
}

/**
 * Snap near-axis edges to exactly axis-aligned.
 *
 * Optional, and off by default: mall plans are full of genuinely angled and
 * radial shopfronts (the whole point of the arcade blocks in the reference
 * plan), and forcing those square is worse than leaving the traced angle.
 */
export function orthogonalise(poly, toleranceDeg = 8) {
    if (poly.length < 3) return poly.slice();
    const tol = (toleranceDeg * Math.PI) / 180;
    const out = poly.map((p) => [p[0], p[1]]);
    for (let i = 0; i < out.length; i++) {
        const a = out[i];
        const b = out[(i + 1) % out.length];
        const dx = b[0] - a[0];
        const dy = b[1] - a[1];
        if (Math.hypot(dx, dy) < 1e-9) continue;
        const angle = Math.atan2(dy, dx);
        const snapped = Math.round(angle / (Math.PI / 2)) * (Math.PI / 2);
        if (Math.abs(angle - snapped) < tol) {
            if (Math.abs(Math.cos(snapped)) < 1e-6) b[0] = a[0];   // vertical
            else b[1] = a[1];                                      // horizontal
        }
    }
    return out;
}

/**
 * Andrew's monotone chain convex hull.
 *
 * Used for SOLIDITY, which is the single most useful shape test for telling a
 * shop from a trace artefact: a tenancy is a compact blob and nearly fills its
 * hull, while a corridor network, an arcade ring or a bled-together background
 * snakes and encloses far less than its hull. Area and aspect ratio both miss
 * those; solidity catches them.
 */
export function convexHull(poly) {
    if (poly.length < 4) return poly.slice();
    const pts = poly.slice().sort((a, b) => (a[0] - b[0]) || (a[1] - b[1]));
    const cross = (o, a, b) => ((a[0] - o[0]) * (b[1] - o[1])) - ((a[1] - o[1]) * (b[0] - o[0]));

    const lower = [];
    pts.forEach((p) => {
        while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], p) <= 0) lower.pop();
        lower.push(p);
    });
    const upper = [];
    for (let i = pts.length - 1; i >= 0; i--) {
        const p = pts[i];
        while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], p) <= 0) upper.pop();
        upper.push(p);
    }
    lower.pop();
    upper.pop();
    return lower.concat(upper);
}

/** Polygon area ÷ convex-hull area. 1 = convex, low = snaking or ring-shaped. */
export function solidity(poly) {
    const a = area(poly);
    if (a <= 0) return 0;
    const hullArea = area(convexHull(poly));
    if (hullArea <= 0) return 0;
    return Math.min(1, a / hullArea);
}

/**
 * Collapse edges shorter than `minLen` by merging their endpoints.
 *
 * RDP alone still leaves 2–3 px stubs where a traced pixel staircase turns a
 * corner, and those read as jagged noise once extruded at mall scale. Merging
 * them is what turns "weird shapes" back into wall lines.
 */
export function mergeShortEdges(poly, minLen) {
    if (poly.length < 4 || minLen <= 0) return poly.slice();
    let pts = poly.map((p) => [p[0], p[1]]);

    let changed = true;
    while (changed && pts.length > 3) {
        changed = false;
        for (let i = 0; i < pts.length; i++) {
            const a = pts[i];
            const b = pts[(i + 1) % pts.length];
            if (Math.hypot(b[0] - a[0], b[1] - a[1]) >= minLen) continue;
            // Fuse the pair into their midpoint and restart the sweep.
            const mid = [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
            const drop = (i + 1) % pts.length;
            const next = [];
            for (let k = 0; k < pts.length; k++) {
                if (k === i || k === drop) continue;
                next.push(pts[k]);
            }
            next.splice(Math.min(i, next.length), 0, mid);
            pts = next;
            changed = true;
            break;
        }
    }
    return pts.length >= 3 ? pts : poly.slice();
}

/** Ensure a consistent winding — THREE.Shape is happier with one direction. */
export function ensureWinding(poly, clockwise = true) {
    const isCw = signedArea(poly) < 0;
    return isCw === clockwise ? poly.slice() : poly.slice().reverse();
}

/**
 * Normalised plan polygon -> mall-space metres, centred on the plan.
 *
 * `metresPerUnit` converts a normalised span to metres, so it is the plan's
 * real WIDTH in metres. Image y grows downward and mall z grows south, so the
 * axes map straight across with no flip.
 */
export function toMallSpace(poly, planWidthM, planHeightM) {
    return poly.map(([nx, ny]) => [
        (nx - 0.5) * planWidthM,
        (ny - 0.5) * planHeightM,
    ]);
}

/** Axis-aligned footprint of a mall-space polygon, for GLA and label placement. */
export function footprint(polyM) {
    const b = bounds(polyM);
    return {
        x: b.minX,
        z: b.minY,
        w: b.w,
        d: b.h,
    };
}
